/**
 * Consent Banner Component
 * GDPR-compliant cookie consent UI (dumb component)
 * Mobile-first, RTL-aware, responsive
 */

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Switch,
  cn,
  Cookie,
  Settings,
  Shield,
  Check,
  X,
} from '@ufc/uikit'
import type { ConsentState } from '../../domain/types'

// ============================================
// Types
// ============================================

export interface ConsentBannerProps {
  /** Current consent state */
  consent: ConsentState
  /** Whether banner should be visible */
  showBanner: boolean
  /** Whether settings modal is open */
  settingsOpen: boolean
  /** Accept all categories */
  onAcceptAll: () => void
  /** Reject non-essential */
  onRejectNonEssential: () => void
  /** Open settings modal */
  onOpenSettings: () => void
  /** Close settings modal */
  onCloseSettings: () => void
  /** Update specific category */
  onUpdateConsent: (category: 'analytics' | 'marketing' | 'preferences', granted: boolean) => void
  /** Save current preferences */
  onSavePreferences: () => void
  /** Custom class name */
  className?: string
}

// ============================================
// Consent Row Component
// ============================================

interface ConsentRowProps {
  title: string
  description: string
  checked: boolean
  disabled?: boolean
  onChange?: (checked: boolean) => void
  icon?: React.ReactNode
}

function ConsentRow({
  title,
  description,
  checked,
  disabled = false,
  onChange,
  icon,
}: ConsentRowProps) {
  return (
    <div className="flex items-center gap-4 py-4 px-1">
      {icon && (
        <div className={cn(
          'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center',
          checked
            ? 'bg-primary/10 text-primary'
            : 'bg-muted text-muted-foreground'
        )}>
          {icon as any}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          {disabled && (
            <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              Required
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        className="shrink-0"
      />
    </div>
  )
}

// ============================================
// Main Component
// ============================================

export function ConsentBanner({
  consent,
  showBanner,
  settingsOpen,
  onAcceptAll,
  onRejectNonEssential,
  onOpenSettings,
  onCloseSettings,
  onUpdateConsent,
  onSavePreferences,
  className,
}: ConsentBannerProps) {
  // Don't render if neither banner nor settings should show
  if (!showBanner && !settingsOpen) return null

  return (
    <>
      {/* Banner */}
      {showBanner && !settingsOpen && (
        <div
          className={cn(
            // Fixed position at bottom with animation
            'fixed bottom-4 start-4 end-4 z-50',
            // Responsive width
            'sm:start-auto sm:end-4 sm:max-w-md',
            // Card styling
            'rounded-2xl shadow-2xl shadow-black/10',
            // Glass effect background
            'bg-background/80 backdrop-blur-xl',
            'border border-border/50',
            // Animation
            'animate-in slide-in-from-bottom-5 fade-in duration-300',
            className
          )}
          role="dialog"
          aria-labelledby="consent-title"
          aria-describedby="consent-description"
        >
          {/* Gradient accent */}
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-blue-500 to-primary rounded-t-2xl" />

          <div className="p-5">
            {/* Header */}
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary">
                <Cookie className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3
                  id="consent-title"
                  className="font-semibold text-base text-foreground"
                >
                  Cookie Preferences
                </h3>
                <p
                  id="consent-description"
                  className="text-sm text-muted-foreground mt-1 leading-relaxed"
                >
                  We use cookies to enhance your browsing experience and analyze site traffic.
                </p>
              </div>
            </div>

            {/* Quick stats */}
            <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-muted/50">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Check className="h-3.5 w-3.5 text-green-500" />
                <span>Essential</span>
              </div>
              <div className="w-px h-4 bg-border" />
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className={consent.analytics ? 'text-green-500' : 'text-muted-foreground'}>
                  {consent.analytics ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                </span>
                <span>Analytics</span>
              </div>
              <div className="w-px h-4 bg-border" />
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className={consent.marketing ? 'text-green-500' : 'text-muted-foreground'}>
                  {consent.marketing ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                </span>
                <span>Marketing</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRejectNonEssential}
                  className="flex-1 h-10"
                >
                  Reject All
                </Button>
                <Button
                  size="sm"
                  onClick={onAcceptAll}
                  className="flex-1 h-10 bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90"
                >
                  Accept All
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onOpenSettings}
                className="w-full h-9 text-muted-foreground hover:text-foreground"
              >
                <Settings className="h-4 w-4 me-2" />
                Customize Preferences
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={(open) => !open && onCloseSettings()}>
        <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden">
          {/* Header with gradient */}
          <div className="relative px-6 pt-6 pb-4 bg-gradient-to-b from-primary/5 to-transparent">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-blue-500 to-primary" />
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-xl">
                <div className="p-2 rounded-xl bg-primary/10 text-primary">
                  <Shield className="h-5 w-5" />
                </div>
                Privacy Settings
              </DialogTitle>
              <DialogDescription className="text-sm leading-relaxed mt-2">
                Manage your cookie preferences. Essential cookies are always active
                as they are required for the website to function properly.
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Cookie categories */}
          <div className="px-6 py-2 divide-y divide-border">
            {/* Necessary - always on */}
            <ConsentRow
              title="Essential Cookies"
              description="Core functionality like security, network management, and accessibility."
              checked={true}
              disabled={true}
              icon={<Shield className="h-5 w-5" />}
            />

            {/* Analytics */}
            <ConsentRow
              title="Analytics Cookies"
              description="Help us understand how visitors interact with our website."
              checked={consent.analytics}
              onChange={(checked) => onUpdateConsent('analytics', checked)}
              icon={
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 20V10M12 20V4M6 20v-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              }
            />

            {/* Preferences */}
            <ConsentRow
              title="Preference Cookies"
              description="Remember your settings like language and region preferences."
              checked={consent.preferences}
              onChange={(checked) => onUpdateConsent('preferences', checked)}
              icon={<Settings className="h-5 w-5" />}
            />

            {/* Marketing */}
            <ConsentRow
              title="Marketing Cookies"
              description="Used to deliver personalized advertisements and promotions."
              checked={consent.marketing}
              onChange={(checked) => onUpdateConsent('marketing', checked)}
              icon={
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              }
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 px-6 py-4 bg-muted/30 border-t border-border">
            <Button
              variant="ghost"
              onClick={onRejectNonEssential}
              className="text-muted-foreground hover:text-foreground"
            >
              Reject All
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onAcceptAll}>
                Accept All
              </Button>
              <Button
                onClick={onSavePreferences}
                className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90"
              >
                Save Preferences
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ============================================
// Export
// ============================================

export default ConsentBanner
